import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import AddObjectModal from "../components/AddObjectModal"
import { useContext, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import ObjectCard from "../components/ObjectCard"
import ObjectActionModal from "../components/ObjectActionModal"
import ModalCheckAction from "../../../components/elements/ModalCheckAction"
import BaseFilterButton from "../../../components/elements/BaseFilterButton"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { useNavigate } from "react-router-dom"
import { checkDate, getDataFromSessionStorage, preventKeyDown, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import { useArchiveDataMutation, useDeleteDataMutation, useRestoreMutation, useStartExcelExportMutation } from "../../api/services"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import DeleteWithZipModal from "../../../components/elements/DeleteWithZipModal"
import { messageError } from "../../../services/messages"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import { ReactComponent as EXPORT } from "../../../assets/DownloadICON.svg"
import { ReactComponent as CIRCLEPLUS } from "../../../assets/PlusInCircleIcon.svg"
import BasePopover from "../../../components/elements/BasePopover"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"

export default function Objekte() {
    // Variables for Pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.OBJECT_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.OBJECT_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // Variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.OBJECT_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm as string, LocalStorageKeys.OBJECT_FILTER + "searchTerm")
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(
        getDataFromSessionStorage(LocalStorageKeys.OBJECT_FILTER + "archived")
    )
    const [filterDelete, setfilterDelete] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.OBJECT_FILTER + "deleted"))

    useResetPageOnFilterChange([searchParams, filterArchived, filterDelete], () => handlePageChange(1))
    
    // Get Data Query
    const queryKey = GeneralQueryKeys.OBJECTS_ALL + page + searchParams + filterArchived + filterDelete
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({ pageType: "object", page: page, limit: 20, search: searchParams, archived: filterArchived, deleted: filterDelete }),
    })

    const navigate = useNavigate()
    // Filter Variables
    const [showFilter, setShowFilter] = useState(false)
    const modalCon = useContext(ModalContext)

    // reset the filter settings
    const handleRemoveFilter = () => {
        setfilterArchived(false)
        setfilterDelete(false)
    }

    // Filter the displayed objects according to filter, user input, usw.
    const displayedObjects = useMemo(() => data?.docs, [data?.docs])

    const queryClient = useQueryClient()
    const [selectedId, setSelectedId] = useState("")

    // Archive Object
    const { mutate: archiveObject } = useArchiveDataMutation(queryClient, () => modalCon?.trigger(3), queryKey, "Objektstatus")
    const handleArchiveObject = (oid: string, archive: boolean) => {
        archiveObject({
            id: oid,
            pageType: "object",
            body: { archived: archive },
        })
    }

    // Delete Object
    const { mutate: deleteObject } = useDeleteDataMutation(queryClient, modalCon, "Objekt", queryKey)
    const handleDeleteObject = () => {
        if (
            displayedObjects?.find((item: { _id: string }) => item._id === selectedId).lastSuccessfulZipExport &&
            checkDate(displayedObjects?.find((item: { _id: string }) => item._id === selectedId).lastSuccessfulZipExport, 7)
        ) {
            deleteObject({ id: selectedId, pageType: "object" })
        } else {
            messageError(
                "a1b2c3",
                "Fehler!",
                "Etwas ist schiefgelaufen. Bitte wenden Sie sich mit einer ausführlichen Problembeschreibung an den Administrator."
            )
        }
    }

    // Start Excel Export of OBJECTS
    const { mutate: startExcelExport } = useStartExcelExportMutation()

    // Restore Object
    const { mutate: restoreObject } = useRestoreMutation(queryClient, "Objekts", queryKey)
    const handleRestoreObject = (id: string) => {
        restoreObject({ id: id, pageType: "object" })
    }

    return (
        <BasePage>
            <HeadBanner
                title={"Objekte"}
                button={
                    <Authorized allOf={[P.objekte_create]}>
                        <div className="flex gap-4">
                            <BasePopover
                                placement="bottomLeft"
                                label={"Export starten"}
                                text="Bei Bestätigung wird eine Excel-Datei an Ihre E-Mail gesendet, welche eine Tabelle aller aktuellen Objekte enthält."
                                okButtonClick={() => startExcelExport({ collectionType: "object" })}>
                                <EXPORT className="w-8 h-8 cursor-pointer" />
                            </BasePopover>
                            <CIRCLEPLUS className="w-8 h-8 cursor-pointer hidden lg:block" onClick={() => modalCon?.trigger(1)} />
                        </div>
                    </Authorized>
                }
            />
            <BaseButtonRow>
                <input
                    onKeyDown={preventKeyDown}
                    onChange={(e) => setsearchTerm(e.target.value)}
                    type="text"
                    value={searchTerm ?? null}
                    placeholder="Objekte suchen..."
                    className="w-full max-w-xs rounded-default bg-none shadow-lg p-4 text-base-200"
                />

                <BaseFilterButton func={() => setShowFilter(!showFilter)} />
                <BaseFilterModal
                    visible={showFilter}
                    setVisible={() => setShowFilter(false)}
                    removeFilter={handleRemoveFilter}
                    statusType={"object"}
                    searchArchived={filterArchived}
                    changeArchived={setfilterArchived}
                    searchDeleted={filterDelete}
                    changeDeleted={setfilterDelete}
                    sessionStorageKey={LocalStorageKeys.OBJECT_FILTER}
                />
            </BaseButtonRow>
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !isLoading && !error && (
                <>
                    {/* BODY */}
                    <BaseGrowBody classProps="pt-4 pb-8 overflow-y-scroll flex flex-col md:inline-grid md:grid-cols-2 lg:grid-cols-3 wxl:grid-cols-4 gap-4 ">
                        {displayedObjects?.map((item: any, index: number) => (
                            <ObjectCard
                                objectDescription={item.objectIndication}
                                costcenter={item.costCenter}
                                key={index}
                                offers={item.offerCount}
                                bills={item.billCount}
                                adress={item.adress}
                                archived={item.archived}
                                deleted={item.softDeleteAfter}
                                openFunc={() => navigate("/objekte/" + item._id)}
                                restoreFunc={() => handleRestoreObject(item._id)}
                                archiveFunc={() => {
                                    handleArchiveObject(item._id, !item.archived)
                                }}
                                deleteFunc={() => {
                                    if (item.lastSuccessfulZipExport && checkDate(item.lastSuccessfulZipExport, 7)) {
                                        setSelectedId(item._id)
                                        modalCon?.trigger(4)
                                    } else {
                                        setSelectedId(item._id)
                                        modalCon?.trigger(6)
                                    }
                                }}
                            />
                        ))}

                        {(!displayedObjects || displayedObjects.length === 0) && (
                            <div className="flex flex-col justify-center items-center w-full">
                                <p className="text-gray-300 uppercase font-medium pt-4">Keine erstellten Objekte</p>
                                {(searchTerm !== "" || searchTerm !== undefined) && (
                                    <button className="btn btn-ghost mt-2 btn-outline max-w-xs" onClick={() => setsearchTerm("")}>
                                        Eingabe zurücksetzen
                                    </button>
                                )}
                            </div>
                        )}
                    </BaseGrowBody>
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            defaultCurrent={1}
                            pageSize={20}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                    <AddObjectModal closeModal={() => modalCon?.trigger(1)} modalId={1} refreshDataUrl={queryKey} />
                    <ObjectActionModal />

                    <ModalCheckAction
                        modalId={4}
                        headline={"Wirklich Löschen?"}
                        text={
                            "Sind Sie sich sicher, dieses Objekt zu löschen? Alle dazugehörigen Daten werden ebenfalls gelöscht. Der Button wird freigegeben, sobald der ZIP-Export heruntergeladen wurde"
                        }
                        buttonText={"Löschen"}
                        buttonColor="btn-error"
                        func={() => handleDeleteObject()}
                        disabled={
                            !displayedObjects?.find((item: { _id: string }) => item._id === selectedId)?.zipExportDownloadedBy ||
                            displayedObjects?.find((item: { _id: string }) => item._id === selectedId)?.zipExportDownloadedBy?.length === 0 ||
                            !displayedObjects?.find((item: { _id: string }) => item._id === selectedId)?.lastSuccessfulZipExport ||
                            !checkDate(displayedObjects?.find((item: { _id: string }) => item._id === selectedId)?.lastSuccessfulZipExport, 7)
                        }
                    />

                    <DeleteWithZipModal
                        modalId={6}
                        title={"Objekt"}
                        queryKey={queryKey}
                        data={{ id: selectedId, pageType: "object" }}
                        closeModal={() => modalCon?.trigger(6)}
                    />
                </>
            )}
        </BasePage>
    )
}
